import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaAndroid,
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaBolt,
  FaChartLine,
  FaCheckCircle,
  FaChevronDown,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import { getProductByKey, products } from "../data/productsData";

const ICONS = {
  "android-app": FaAndroid,
  "rugshield-web": FaShieldAlt,
  social: FaUsers,
  nova: FaBolt,
};

const floatA = keyframes`
  0%,100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-22px) scale(1.04); }
`;
const floatB = keyframes`
  0%,100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(18px) scale(0.97); }
`;
const floatC = keyframes`
  0%,100% { transform: translateY(0) translateX(0); }
  33% { transform: translateY(-14px) translateX(10px); }
  66% { transform: translateY(8px) translateX(-6px); }
`;
const scanMoveDetail = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateY(100vh); opacity: 0; }
`;
const scanMoveDetail2 = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 0.6; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(100vh); opacity: 0; }
`;
const gradientShiftDetail = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const shimmerDetail = keyframes`
  0%   { transform: translateX(-200%) skewX(-18deg); }
  100% { transform: translateX(400%) skewX(-18deg); }
`;
const pulseDotDetail = keyframes`
  0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(41,171,226,0.55); }
  50%      { transform: scale(1.2); box-shadow: 0 0 0 7px rgba(41,171,226,0); }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const heroStats = [
  ["83%+", "Win Rate"],
  ["2000+", "Signals Delivered"],
  ["325+", "TP1 Hits"],
  ["825+", "TP2 Hits"],
];

const featureBlocks = [
  [FaBolt, "Real-Time AI Signals Engine", "AI detects setups with exact entry, TP1, TP2, and stop-loss levels so users can see a full trade plan instead of a vague alert."],
  [FaBell, "Instant Push Notifications", "High-probability setups are pushed instantly so traders do not need to sit on charts all day waiting for movement."],
  [FaChartLine, "Live Charts and Heatmaps", "The app combines signals with visual market context through charts, trend views, and faster heatmap scanning."],
  [FaLayerGroup, "Watchlists and Portfolio Tracking", "Users can track favorite assets, review active signals, and manage a cleaner mobile trading workflow from one dashboard."],
];

const compareRows = [
  ["Signal Speed", "Milliseconds (95%)", "Hours (30%)"],
  ["Consistency", "88%", "45%"],
  ["Emotional Stress", "15%", "85%"],
];

const steps = [
  "Download the app from Google Play.",
  "Sign up through a quick and secure onboarding flow.",
  "Use the 7-day full-access trial to test every feature risk-free.",
  "Receive your first AI signals and build your watchlists.",
  "Continue after the trial for just $10 per month.",
];

const rugshieldStats = [
  ["Free", "Always Free"],
  ["2-5s", "Typical Scan Time"],
  ["2", "Supported Chains"],
  ["Read-Only", "No Wallet Needed"],
];

const rugshieldFeatures = [
  [FaShieldAlt, "AI-Assisted Traffic Light Scoring", "RugShield combines multiple checks into one simple green, amber, or red safety snapshot so users can spot risk faster."],
  [FaBolt, "Honeypot Detection", "The scanner flags contracts that may allow buying while restricting or blocking selling, which is one of the most dangerous DeFi traps."],
  [FaLayerGroup, "Ownership and Rug Pattern Analysis", "It checks for centralized ownership, risky permissions, and contract patterns that often appear before rug pulls."],
  [FaUsers, "Beginner and Pro Modes", "New users can read plain-English explanations while experienced traders can jump straight into deeper technical detail."],
  [FaChartLine, "Lightning-Fast Scan Reports", "Most scans complete in roughly 2 to 5 seconds using public blockchain data and external risk signals."],
  [FaCheckCircle, "Read-Only Security", "RugShield never requests a wallet connection, approval, or signature, so users can scan tokens without exposing funds."],
];

const rugshieldSteps = [
  "Find the contract address from Etherscan or BscScan.",
  "Paste it into RugShield at app.trendgenie.io.",
  "Select Ethereum or BNB Chain.",
  "Choose Beginner mode or Pro mode.",
  "Run the scan and review the traffic-light report plus signal cards.",
];

const rugshieldCompareRows = [
  ["Pricing", "Completely free", "Free, limited, or paid"],
  ["Chains Supported", "Ethereum and BNB Chain", "Often one chain or a mixed experience"],
  ["Explanation Modes", "Beginner and Pro", "Usually one style only"],
  ["Scan Speed", "2 to 5 seconds", "Varies and can be slower"],
  ["Wallet Connection", "Never required", "Sometimes required"],
  ["Educational Focus", "Strong learning-first approach", "Often secondary"],
];

const rugshieldRoadmap = [
  ["Phase 1", "In Testing", "LP lock status, liquidity health, and deployer share analysis."],
  ["Phase 2", "Design", "Top holders distribution, whale warnings, and CEX or LP tagging."],
  ["Phase 3", "Planned", "Behavioral alerts, holder spikes, and notification options."],
];

function GenericPage({ product, relatedProducts, Icon }) {
  return (
    <Wrap>
      <Helmet>
        <title>{product.seoTitle}</title>
        <meta name="description" content={product.metaDescription} />
        <meta name="keywords" content={[product.primaryKeyword, ...(product.secondaryKeywords || [])].join(", ")} />
        <link rel="canonical" href={`https://www.trendgenie.io${product.path}`} />
      </Helmet>
      <Hero data-tone="dark">
        <OrbA /><OrbB /><OrbC />
        <HeroSL1 /><HeroSL2 />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <Bread><Link to="/products"><FaArrowLeft /> Products</Link><span>/</span><span>{product.shortName}</span></Bread>
          <HeroGrid>
            <div>
              <Badge><span>{product.heroKicker}</span></Badge>
              <Title>{product.name}</Title>
              <Sub>{product.overview}</Sub>
              <Actions>
                <Primary href={product.externalUrl} target="_blank" rel="noopener noreferrer">Open {product.shortName} <FaExternalLinkAlt /></Primary>
                <Ghost to="/products"><FaArrowLeft /> All Products</Ghost>
              </Actions>
            </div>
            <Card>
              <MiniIcon><Icon /></MiniIcon>
              <h3>Quick Summary</h3>
              <List>
                <li><strong>Status</strong><span>{product.statusLabel}</span></li>
                <li><strong>Access</strong><span>{product.domainLabel}</span></li>
                <li><strong>Topic</strong><span>{product.primaryKeyword}</span></li>
              </List>
            </Card>
          </HeroGrid>
        </Container>
      </Hero>

      <Container style={{ paddingTop: "2rem" }}>
        <Grid2>
          <Card><h3>Why This Product Matters</h3><p>{product.whyItMatters}</p></Card>
          <Card><h3>Key Features</h3><Bullet>{product.features.map((x) => <li key={x}><FaCheckCircle />{x}</li>)}</Bullet></Card>
          <Card><h3>Who This Product Is For</h3><Bullet>{product.bestFor.map((x) => <li key={x}><FaCheckCircle />{x}</li>)}</Bullet></Card>
          <Card><h3>Access and Availability</h3><Bullet>{product.access.map((x) => <li key={x}><FaCheckCircle />{x}</li>)}</Bullet></Card>
        </Grid2>
        {!!relatedProducts.length && <Card style={{ marginTop: "1rem" }}><h3>Related Products</h3><Related>{relatedProducts.map((rel) => <Link key={rel.key} to={rel.path}>{rel.name} <FaArrowRight /></Link>)}</Related></Card>}
      </Container>
    </Wrap>
  );
}

function AndroidPage({ product, relatedProducts }) {
  const [openFaq, setOpenFaq] = useState(null);
  const faqItems = product.faqs;
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <AndroidWrap>
      <Helmet>
        <title>{product.seoTitle}</title>
        <meta name="description" content={product.metaDescription} />
        <meta name="keywords" content={[product.primaryKeyword, ...(product.secondaryKeywords || [])].join(", ")} />
        <link rel="canonical" href={`https://www.trendgenie.io${product.path}`} />
        <meta property="og:title" content={product.seoTitle} />
        <meta property="og:description" content={product.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <Hero data-tone="dark">
        <OrbA /><OrbB /><OrbC />
        <HeroSL1 /><HeroSL2 />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <Bread><Link to="/products"><FaArrowLeft /> Products</Link><span>/</span><span>AI Crypto Signals App</span></Bread>
          <HeroGrid>
            <div>
              <Badge><span>Flagship Android Product</span></Badge>
              <Title>Trend Genie AI Crypto Signals App — Trade Smarter on Android</Title>
              <Sub>Real-time AI-powered crypto signals with precise entry points, TP1 and TP2 targets, stop-loss levels, and instant push notifications. The mobile-first app that removes emotion and guesswork from trading.</Sub>
              <Trust><FaCheckCircle /> 83%+ Win Rate <span>|</span> +8909.4% Portfolio Growth Example <span>|</span> 2000+ Signals Delivered</Trust>
              <Actions>
                <Primary href={product.externalUrl} target="_blank" rel="noopener noreferrer">Start Your 7-Day Free Trial on Google Play <FaArrowRight /></Primary>
                <Ghost to="/rugshield-defi-scanner">Use RugShield First <FaArrowRight /></Ghost>
              </Actions>
              <Small>5-star rated · 10,000+ traders · No credit card needed for trial</Small>
            </div>
            <HeroPanel>
              <PanelLabel>Live Signal Snapshot</PanelLabel>
              <SignalBox>
                <div><strong>BTC / USDT</strong><small>AI LONG setup</small></div>
                <em>High Confidence</em>
              </SignalBox>
              <SignalGrid>
                <InfoMini><small>Entry</small><strong>$67,420</strong></InfoMini>
                <InfoMini><small>TP1</small><strong>$69,800</strong></InfoMini>
                <InfoMini><small>TP2</small><strong>$72,100</strong></InfoMini>
                <InfoMini><small>Stop Loss</small><strong>$65,900</strong></InfoMini>
              </SignalGrid>
            </HeroPanel>
          </HeroGrid>
        </Container>
      </Hero>

      {/* ── STATS ── */}
      <PageSection>
        <Container>
          <StatGrid>
            {heroStats.map(([v, l], i) => (
              <AiStatCard
                key={l}
                as={motion.div}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <AiStatNum>{v}</AiStatNum>
                <AiStatLabel>{l}</AiStatLabel>
              </AiStatCard>
            ))}
          </StatGrid>
        </Container>
      </PageSection>

      {/* ── WHY CHOOSE + PRICING ── */}
      <PageSection $alt>
        <Container>
          <SectionHd
            as={motion.div}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <SectionTag>Performance</SectionTag>
            <SecTitle>Why Traders Choose Us in 2026</SecTitle>
          </SectionHd>
          <TwoCol>
            <AiCard as={motion.div} custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Why Trend Genie Beats Manual Trading</AiCardTitle>
              <AiCardText>In the fast-moving crypto market, timing is everything. Trend Genie delivers real-time AI signals directly to your Android device, helping you spot high-probability setups for Bitcoin, Ethereum, and 1000+ altcoins faster than manual analysis.</AiCardText>
              <AiCardText>Unlike generic alerts, every signal comes with a complete trade plan — entry price, TP1, TP2, and protective stop-loss. No more emotional decisions or missed opportunities.</AiCardText>
              <AiBullet>
                <li><FaCheckCircle />83%+ historical win rate on tracked signals</li>
                <li><FaCheckCircle />Over 2,000 signals fired since launch</li>
                <li><FaCheckCircle />325+ TP1 hits · 825+ TP2 hits</li>
                <li><FaCheckCircle />Example portfolio growth: +8,909.4%</li>
              </AiBullet>
            </AiCard>
            <AiCard as={motion.div} custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Simple Pricing That Delivers Real Value</AiCardTitle>
              <AiBullet>
                <li><FaCheckCircle />7-Day Free Trial — complete access to all signals, alerts, charts, and tools</li>
                <li><FaCheckCircle />Only $10 per month after the trial period</li>
                <li><FaCheckCircle />No hidden fees. Pause or cancel anytime.</li>
              </AiBullet>
              <AiCardTitle style={{ marginTop: "1.5rem" }}>Who Benefits Most?</AiCardTitle>
              <AiBullet>{product.bestFor.map((x) => <li key={x}><FaCheckCircle />{x}</li>)}</AiBullet>
            </AiCard>
          </TwoCol>
        </Container>
      </PageSection>

      {/* ── FEATURES ── */}
      <PageSection>
        <Container>
          <SectionHd
            as={motion.div}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <SectionTag>Features</SectionTag>
            <SecTitle>Powerful Features That Give You an Edge</SecTitle>
          </SectionHd>
          <FeatGrid>
            {featureBlocks.map(([Ico, title, text], i) => (
              <FeatCard
                key={title}
                as={motion.div}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                style={{ "--delay": `${i * 1.2}s` }}
              >
                <FeatIconBox><Ico /></FeatIconBox>
                <FeatCardTitle>{title}</FeatCardTitle>
                <FeatCardText>{text}</FeatCardText>
              </FeatCard>
            ))}
          </FeatGrid>
        </Container>
      </PageSection>

      {/* ── COMPARISON + STEPS ── */}
      <PageSection $alt>
        <Container>
          <TwoCol>
            <AiCard as={motion.div} custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>AI vs Manual Trading — See the Difference</AiCardTitle>
              <CmpTable>
                <thead>
                  <tr>
                    <CmpHead>Aspect</CmpHead>
                    <CmpHead $accent="#29ABE2">Trend Genie AI</CmpHead>
                    <CmpHead $accent="#ef4444">Manual Trading</CmpHead>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((r) => (
                    <tr key={r[0]}>
                      <CmpCell>{r[0]}</CmpCell>
                      <CmpCell $good>{r[1]}</CmpCell>
                      <CmpCell $bad>{r[2]}</CmpCell>
                    </tr>
                  ))}
                </tbody>
              </CmpTable>
            </AiCard>
            <AiCard as={motion.div} custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>How to Start Trading with AI Signals in Minutes</AiCardTitle>
              <StepsTimeline>
                {steps.map((s, i) => (
                  <StepRow key={s} $last={i === steps.length - 1}>
                    <StepNumCircle>{i + 1}</StepNumCircle>
                    <StepContent>{s}</StepContent>
                  </StepRow>
                ))}
              </StepsTimeline>
            </AiCard>
          </TwoCol>
        </Container>
      </PageSection>

      {/* ── ECOSYSTEM FLOW ── */}
      <PageSection>
        <Container>
          <SectionHd
            as={motion.div}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <SectionTag>Ecosystem</SectionTag>
            <SecTitle>How the Ecosystem Flow Works</SecTitle>
          </SectionHd>
          <FlowConnected>
            {[
              { num: "01", text: "Download the AI Signals App" },
              { num: "02", text: "Scan tokens with RugShield before investing" },
              { num: "03", text: "Discuss setups in the Social community" },
              { num: "04", text: "Earn $TGEN and level up your workflow" },
            ].map((step, i, arr) => (
              <React.Fragment key={step.num}>
                <FlowStepCard
                  as={motion.div}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <FlowStepNum>{step.num}</FlowStepNum>
                  <FlowStepText>{step.text}</FlowStepText>
                </FlowStepCard>
                {i < arr.length - 1 && <FlowConnector><FaArrowRight /></FlowConnector>}
              </React.Fragment>
            ))}
          </FlowConnected>
          <EcoRelated>
            <Link to="/rugshield-defi-scanner">RugShield Scanner <FaArrowRight /></Link>
            <Link to="/products/social">Trend Genie Social <FaArrowRight /></Link>
            <Link to="/products/nova">Nova AI Platform <FaArrowRight /></Link>
          </EcoRelated>
        </Container>
      </PageSection>

      {/* ── FAQ ── */}
      <PageSection $alt>
        <Container>
          <SectionHd
            as={motion.div}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <SectionTag>FAQ</SectionTag>
            <SecTitle>Frequently Asked Questions</SecTitle>
          </SectionHd>
          <FaqGrid>
            {faqItems.map((item, i) => {
              const open = openFaq === i;
              return (
                <FaqItemCard
                  key={item.question}
                  as={motion.div}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  $open={open}
                >
                  <FaqItemTrigger type="button" onClick={() => setOpenFaq(open ? null : i)}>
                    <FaqItemQ>{item.question}</FaqItemQ>
                    <FaqChevronIcon $open={open}><FaChevronDown /></FaqChevronIcon>
                  </FaqItemTrigger>
                  <AnimatePresence initial={false}>
                    {open && (
                      <AndroidFaqBody
                        key="ans"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <FaqItemA>{item.answer}</FaqItemA>
                      </AndroidFaqBody>
                    )}
                  </AnimatePresence>
                </FaqItemCard>
              );
            })}
          </FaqGrid>
        </Container>
      </PageSection>

      {/* ── CTA BANNER ── */}
      <CtaBanner>
        <CtaOrbA /><CtaOrbB />
        <CtaScanLine />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <CtaInner>
            <CtaBannerTag>Get Started Today</CtaBannerTag>
            <CtaBannerTitle>Ready to Level Up Your Trading?</CtaBannerTitle>
            <CtaBannerSub>Start your 7-day free trial now, then strengthen your workflow with RugShield for token safety and Trend Genie Social for community rewards.</CtaBannerSub>
            <Actions style={{ justifyContent: "center" }}>
              <Primary href={product.externalUrl} target="_blank" rel="noopener noreferrer">Start Your 7-Day Free Trial <FaArrowRight /></Primary>
              <Ghost to="/rugshield-defi-scanner">Open RugShield Scanner <FaArrowRight /></Ghost>
              <Ghost to="/products/social">Join Social &amp; Earn TGEN <FaArrowRight /></Ghost>
            </Actions>
          </CtaInner>
        </Container>
      </CtaBanner>

      {!!relatedProducts.length && (
        <PageSection>
          <Container>
            <AiCardTitle style={{ marginBottom: "1rem" }}>Related Product Pages</AiCardTitle>
            <EcoRelated>
              {relatedProducts.map((rel) => (
                <Link key={rel.key} to={rel.path}>{rel.name} <FaArrowRight /></Link>
              ))}
            </EcoRelated>
          </Container>
        </PageSection>
      )}
    </AndroidWrap>
  );
}

function RugShieldPage({ product, relatedProducts }) {
  const [openFaq, setOpenFaq] = useState(null);
  const faqItems = product.faqs;
  const appSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: product.name,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    description: product.metaDescription,
    url: `https://www.trendgenie.io${product.path}`,
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <AndroidWrap>
      <Helmet>
        <title>{product.seoTitle}</title>
        <meta name="description" content={product.metaDescription} />
        <meta name="keywords" content={[product.primaryKeyword, ...(product.secondaryKeywords || [])].join(", ")} />
        <link rel="canonical" href={`https://www.trendgenie.io${product.path}`} />
        <meta property="og:title" content={product.seoTitle} />
        <meta property="og:description" content={product.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Hero data-tone="dark">
        <OrbA /><OrbB /><OrbC />
        <HeroSL1 /><HeroSL2 />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <Bread><Link to="/products"><FaArrowLeft /> Products</Link><span>/</span><span>RugShield DeFi Scanner</span></Bread>
          <HeroGrid>
            <div>
              <Badge><span>DeFi Safety Scanner</span></Badge>
              <Title>RugShield DeFi Scanner - Stay Safe Before You Ape In</Title>
              <Sub>The free AI-assisted DeFi safety tool that turns complex on-chain checks into a simple traffic-light score. Detect rug pulls, honeypots, and high-risk contracts on Ethereum and BNB Chain in seconds.</Sub>
              <Trust><FaCheckCircle /> Read-only <span>|</span> No wallet needed <span>|</span> Beginner and Pro modes <span>|</span> 2 to 5 second scans</Trust>
              <Actions>
                <Primary href={product.externalUrl} target="_blank" rel="noopener noreferrer">Scan Any Token Free Now <FaArrowRight /></Primary>
                <Ghost to="/ai-crypto-signals-app">Use With Trend Genie App <FaArrowRight /></Ghost>
              </Actions>
              <Small>Educational tool · Trusted by thousands of DeFi users · Beta version with more signals coming soon</Small>
            </div>
            <HeroPanel>
              <PanelLabel>Risk Snapshot Example</PanelLabel>
              <SignalBox>
                <div><strong>Token Risk Score</strong><small>Ethereum contract scan</small></div>
                <em style={{ color: "#fbbf24" }}>Amber Review</em>
              </SignalBox>
              <SignalGrid>
                <InfoMini><small>Honeypot</small><strong>Not Detected</strong></InfoMini>
                <InfoMini><small>Ownership</small><strong>Review Needed</strong></InfoMini>
                <InfoMini><small>Wallet</small><strong>Not Required</strong></InfoMini>
                <InfoMini><small>Mode</small><strong>Beginner / Pro</strong></InfoMini>
              </SignalGrid>
            </HeroPanel>
          </HeroGrid>
        </Container>
      </Hero>

      <PageSection>
        <Container>
          <StatGrid>
            {rugshieldStats.map(([v, l], i) => (
              <AiStatCard
                key={l}
                as={motion.div}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <AiStatNum>{v}</AiStatNum>
                <AiStatLabel>{l}</AiStatLabel>
              </AiStatCard>
            ))}
          </StatGrid>
        </Container>
      </PageSection>

      <PageSection $alt>
        <Container>
          <SectionHd as={motion.div} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTag>Why RugShield</SectionTag>
            <SecTitle>Why You Need a Reliable DeFi Safety Scanner in 2026</SecTitle>
          </SectionHd>
          <TwoCol>
            <AiCard as={motion.div} custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Your First Line of Defense Before Buying</AiCardTitle>
              <AiCardText>The DeFi space moves fast and thousands of new tokens launch every week, but not all of them are legitimate. Rug pulls, honeypots, and malicious smart contracts still drain millions from users who buy first and verify later.</AiCardText>
              <AiCardText>RugShield by Trend Genie helps solve that problem with a read-only scanner that analyzes token contracts on Ethereum and BNB Chain, then returns a clear traffic-light score with explanation cards you can actually understand.</AiCardText>
              <AiBullet>
                <li><FaCheckCircle />Spot potential rug pulls and honeypots before they cost you money</li>
                <li><FaCheckCircle />Understand ownership risks and suspicious contract behavior</li>
                <li><FaCheckCircle />Keep your funds safe because there is no wallet connection or approval flow</li>
                <li><FaCheckCircle />Use it with the Trend Genie app for a safer trading workflow</li>
              </AiBullet>
            </AiCard>
            <AiCard as={motion.div} custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>How to Scan a Token with RugShield in Under 30 Seconds</AiCardTitle>
              <StepsTimeline>
                {rugshieldSteps.map((s, i) => (
                  <StepRow key={s} $last={i === rugshieldSteps.length - 1}>
                    <StepNumCircle>{i + 1}</StepNumCircle>
                    <StepContent>{s}</StepContent>
                  </StepRow>
                ))}
              </StepsTimeline>
              <AiCardText style={{ marginTop: "1rem" }}>Pro tip: always cross-check important results directly on Etherscan or BscScan. RugShield is an educational early-warning tool, not a guarantee.</AiCardText>
            </AiCard>
          </TwoCol>
        </Container>
      </PageSection>

      <PageSection>
        <Container>
          <SectionHd as={motion.div} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTag>Features</SectionTag>
            <SecTitle>Powerful Features That Keep You Protected</SecTitle>
          </SectionHd>
          <FeatGrid>
            {rugshieldFeatures.map(([Ico, title, text], i) => (
              <FeatCard
                key={title}
                as={motion.div}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                style={{ "--delay": `${i * 1.2}s` }}
              >
                <FeatIconBox><Ico /></FeatIconBox>
                <FeatCardTitle>{title}</FeatCardTitle>
                <FeatCardText>{text}</FeatCardText>
              </FeatCard>
            ))}
          </FeatGrid>
        </Container>
      </PageSection>

      <PageSection $alt>
        <Container>
          <TwoCol>
            <AiCard as={motion.div} custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>RugShield vs Other DeFi Rug Checkers</AiCardTitle>
              <CmpTable>
                <thead>
                  <tr>
                    <CmpHead>Feature</CmpHead>
                    <CmpHead $accent="#10b981">RugShield</CmpHead>
                    <CmpHead $accent="#f59e0b">Typical Other Scanners</CmpHead>
                  </tr>
                </thead>
                <tbody>
                  {rugshieldCompareRows.map((row) => (
                    <tr key={row[0]}>
                      <CmpCell>{row[0]}</CmpCell>
                      <CmpCell $good>{row[1]}</CmpCell>
                      <CmpCell>{row[2]}</CmpCell>
                    </tr>
                  ))}
                </tbody>
              </CmpTable>
              <AiCardText style={{ marginTop: "1rem" }}>RugShield stands out by balancing simplicity, speed, and education while staying completely free.</AiCardText>
            </AiCard>
            <AiCard as={motion.div} custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Coming Soon - Expanded Risk Intelligence</AiCardTitle>
              <StepsTimeline>
                {rugshieldRoadmap.map(([phase, status, text], i) => (
                  <StepRow key={phase} $last={i === rugshieldRoadmap.length - 1}>
                    <StepNumCircle>{phase.replace("Phase ", "")}</StepNumCircle>
                    <StepContent><strong>{status}</strong><br />{text}</StepContent>
                  </StepRow>
                ))}
              </StepsTimeline>
            </AiCard>
          </TwoCol>
        </Container>
      </PageSection>

      <PageSection>
        <Container>
          <SectionHd as={motion.div} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTag>Use Cases</SectionTag>
            <SecTitle>How Traders Use RugShield Every Day</SecTitle>
          </SectionHd>
          <TwoCol>
            <AiCard as={motion.div} custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Real-World Checks Before Every Ape</AiCardTitle>
              <AiBullet>
                <li><FaCheckCircle />Before buying a new meme coin, paste the contract and check for honeypot risk</li>
                <li><FaCheckCircle />Verify trending tokens discovered through the Trend Genie app</li>
                <li><FaCheckCircle />Test safe tokens like USDT or USDC to understand what a cleaner report looks like</li>
                <li><FaCheckCircle />Share scan results with friends or the community to discuss red flags</li>
              </AiBullet>
              <AiCardText style={{ marginTop: "1rem" }}>"I almost aped into a new token that looked promising until RugShield showed amber ownership risks." That is exactly the kind of preventable mistake this tool is built to reduce.</AiCardText>
            </AiCard>
            <AiCard as={motion.div} custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <AiCardTitle>Complete the Trend Genie Ecosystem</AiCardTitle>
              <AiBullet>
                <li><FaCheckCircle />Get real-time AI crypto signals in the Android app with a 7-day free trial, then $10 per month</li>
                <li><FaCheckCircle />Join Trend Genie Social to discuss safer tokens and earn TGEN rewards</li>
                <li><FaCheckCircle />Use GenieNova for deeper research before bigger moves</li>
              </AiBullet>
              <EcoRelated>
                <Link to="/ai-crypto-signals-app">AI Crypto Signals App <FaArrowRight /></Link>
                <Link to="/products/social">Trend Genie Social <FaArrowRight /></Link>
                <Link to="/products/nova">GenieNova <FaArrowRight /></Link>
              </EcoRelated>
            </AiCard>
          </TwoCol>
        </Container>
      </PageSection>

      <PageSection $alt>
        <Container>
          <SectionHd as={motion.div} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTag>FAQ</SectionTag>
            <SecTitle>Frequently Asked Questions About RugShield DeFi Scanner</SecTitle>
          </SectionHd>
          <FaqGrid>
            {faqItems.map((item, i) => {
              const open = openFaq === i;
              return (
                <FaqItemCard
                  key={item.question}
                  as={motion.div}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  $open={open}
                >
                  <FaqItemTrigger type="button" onClick={() => setOpenFaq(open ? null : i)}>
                    <FaqItemQ>{item.question}</FaqItemQ>
                    <FaqChevronIcon $open={open}><FaChevronDown /></FaqChevronIcon>
                  </FaqItemTrigger>
                  <AnimatePresence initial={false}>
                    {open && (
                      <AndroidFaqBody
                        key="ans"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <FaqItemA>{item.answer}</FaqItemA>
                      </AndroidFaqBody>
                    )}
                  </AnimatePresence>
                </FaqItemCard>
              );
            })}
          </FaqGrid>
        </Container>
      </PageSection>

      <CtaBanner>
        <CtaOrbA /><CtaOrbB />
        <CtaScanLine />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <CtaInner>
            <CtaBannerTag>Scan Before You Buy</CtaBannerTag>
            <CtaBannerTitle>Ready to Scan Smarter and Trade Safer?</CtaBannerTitle>
            <CtaBannerSub>Do not ape blindly. Make RugShield part of your pre-trade checklist today, then connect it with the rest of the Trend Genie ecosystem.</CtaBannerSub>
            <Actions style={{ justifyContent: "center" }}>
              <Primary href={product.externalUrl} target="_blank" rel="noopener noreferrer">Open RugShield and Scan a Token <FaArrowRight /></Primary>
              <Ghost to="/ai-crypto-signals-app">Explore AI Signals App <FaArrowRight /></Ghost>
              <Ghost to="/products/social">Join Social and Earn TGEN <FaArrowRight /></Ghost>
            </Actions>
            <Small style={{ marginTop: "1rem" }}>Beta educational tool by Trend Genie · Thousands of scans performed · Focused on user safety and learning</Small>
          </CtaInner>
        </Container>
      </CtaBanner>

      {!!relatedProducts.length && (
        <PageSection>
          <Container>
            <AiCardTitle style={{ marginBottom: "1rem" }}>Related Product Pages</AiCardTitle>
            <EcoRelated>
              {relatedProducts.map((rel) => (
                <Link key={rel.key} to={rel.path}>{rel.name} <FaArrowRight /></Link>
              ))}
            </EcoRelated>
          </Container>
        </PageSection>
      )}
    </AndroidWrap>
  );
}

function ProductDetailPage({ productKey }) {
  const product = getProductByKey(productKey);
  const relatedProducts = useMemo(() => products.filter((p) => product && product.relatedKeys.includes(p.key)), [product]);
  if (!product) return null;
  const Icon = ICONS[product.key] || FaBolt;
  if (product.key === "android-app") {
    return <AndroidPage product={product} relatedProducts={relatedProducts} />;
  }
  if (product.key === "rugshield-web") {
    return <RugShieldPage product={product} relatedProducts={relatedProducts} />;
  }
  return <GenericPage product={product} relatedProducts={relatedProducts} Icon={Icon} />;
}

export default ProductDetailPage;

const Wrap = styled.div`background: var(--bg-main); color: var(--text-color); padding-bottom: 6rem; overflow-x: hidden;`;
const Container = styled.div`padding: 0 10%;`;

/* ── Hero dark futuristic ── */
const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #040c16 0%, #071525 40%, #08182a 70%, #060e1a 100%);
  padding: 4.5rem 0 4rem;
  min-height: 520px;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(41,171,226,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(41,171,226,0.06) 1px, transparent 1px);
    background-size: 50px 50px;
    mask-image: radial-gradient(ellipse 85% 80% at 30% 50%, black 8%, transparent 65%);
    pointer-events: none;
  }
`;

const HeroSL1 = styled.div`
  position: absolute; left: 0; width: 100%; height: 1.5px;
  background: linear-gradient(90deg, transparent 0%, rgba(41,171,226,0.55) 30%, rgba(247,148,29,0.3) 60%, transparent 100%);
  animation: ${scanMoveDetail} 5s linear infinite;
  pointer-events: none; z-index: 1;
`;
const HeroSL2 = styled.div`
  position: absolute; left: 0; width: 100%; height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.45) 40%, rgba(41,171,226,0.25) 70%, transparent 100%);
  animation: ${scanMoveDetail2} 8s linear infinite 3s;
  pointer-events: none; z-index: 1;
`;

const OrbA = styled.div`
  position: absolute; width: 480px; height: 480px; border-radius: 50%;
  background: radial-gradient(circle, rgba(41,171,226,0.22) 0%, transparent 65%);
  filter: blur(70px);
  top: -140px; right: -80px;
  animation: ${floatA} 10s ease-in-out infinite;
  pointer-events: none;
`;
const OrbB = styled.div`
  position: absolute; width: 360px; height: 360px; border-radius: 50%;
  background: radial-gradient(circle, rgba(247,148,29,0.16) 0%, transparent 65%);
  filter: blur(70px);
  left: -60px; bottom: -90px;
  animation: ${floatB} 12s ease-in-out infinite;
  pointer-events: none;
`;
const OrbC = styled.div`
  position: absolute; width: 240px; height: 240px; border-radius: 50%;
  background: radial-gradient(circle, rgba(139,92,246,0.14) 0%, transparent 65%);
  filter: blur(60px);
  top: 30%; left: 38%;
  animation: ${floatC} 9s ease-in-out infinite 2s;
  pointer-events: none;
`;

const Bread = styled.nav`
  display: flex; align-items: center; gap: .5rem; margin-bottom: 1.5rem;
  color: rgba(160,195,218,0.55); font-size: .84rem;
  a { color: #29ABE2; text-decoration: none; display: inline-flex; align-items: center; gap: .4rem;
      transition: color .2s; &:hover { color: #7dd3f8; } }
`;
const HeroGrid = styled.div`display:grid;grid-template-columns:1.1fr .9fr;gap:1.5rem;align-items:center;@media (max-width:980px){grid-template-columns:1fr;}`;

/* Badge: pill with pulsing live dot */
const Badge = styled.div`
  display: inline-flex; align-items: center; gap: .45rem;
  padding: .32rem .9rem .32rem .5rem; border-radius: 999px;
  background: rgba(41,171,226,.1); border: 1px solid rgba(41,171,226,.28);
  margin-bottom: 1.1rem; width: fit-content;

  &::before {
    content: "";
    display: inline-block; width: 8px; height: 8px; border-radius: 50%;
    background: #29ABE2; flex-shrink: 0;
    animation: ${pulseDotDetail} 1.8s ease-in-out infinite;
  }

  span {
    color: #29ABE2; font-size: .72rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: .12em;
  }
`;

const Title = styled.h1`
  margin: 0 0 .95rem;
  font-size: clamp(2rem, 4.5vw, 3.6rem);
  line-height: 1.06; letter-spacing: -.035em; font-weight: 900;
  background: linear-gradient(135deg, #ffffff 0%, #cce9f8 35%, #29ABE2 65%, #F7941D 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text; -webkit-text-fill-color: transparent !important;
  background-clip: text;
  animation: ${gradientShiftDetail} 6s ease infinite;
`;

const Sub = styled.p`
  margin: 0 0 1rem; max-width: 720px;
  color: rgba(200,225,240,0.72) !important;
  -webkit-text-fill-color: rgba(200,225,240,0.72) !important;
  line-height: 1.82; font-size: 1rem;
`;

const Trust = styled.div`
  display: flex; flex-wrap: wrap; gap: .5rem .8rem; align-items: center;
  color: rgba(200,235,248,0.85); font-size: .9rem; font-weight: 700;
  margin-bottom: 1.4rem;
  svg { color: #10b981; }
  span { opacity: .5; }
`;

const Small = styled.p`
  margin: .6rem 0 0;
  color: rgba(170,205,225,0.55) !important;
  -webkit-text-fill-color: rgba(170,205,225,0.55) !important;
  font-size: .85rem; font-weight: 500;
`;

const Actions = styled.div`display:flex;flex-wrap:wrap;gap:.8rem;margin-top:1.2rem;`;

const Primary = styled.a`
  display: inline-flex; align-items: center; gap: .55rem;
  padding: .9rem 1.4rem; border-radius: 100px;
  background: linear-gradient(135deg, #29ABE2 0%, #1a7fad 100%);
  color: #fff !important; -webkit-text-fill-color: #fff !important;
  text-decoration: none; font-weight: 700; font-size: .93rem;
  box-shadow: 0 4px 24px rgba(41,171,226,.38);
  position: relative; overflow: hidden;
  transition: transform .22s ease, box-shadow .22s ease;

  &::after {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.22), transparent);
    transform: translateX(-150%) skewX(-15deg);
  }
  &:hover {
    transform: translateY(-2px); box-shadow: 0 8px 36px rgba(41,171,226,.5);
    &::after { animation: ${shimmerDetail} .55s ease forwards; }
  }
`;

const Ghost = styled(Link)`
  display: inline-flex; align-items: center; gap: .55rem;
  padding: .88rem 1.3rem; border-radius: 100px;
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.14);
  color: rgba(210,235,248,.88) !important; -webkit-text-fill-color: rgba(210,235,248,.88) !important;
  text-decoration: none; font-weight: 600; font-size: .92rem;
  transition: background .22s ease, border-color .22s ease, transform .22s ease;
  &:hover { background: rgba(41,171,226,.1); border-color: rgba(41,171,226,.4); transform: translateY(-2px); }
`;

/* Hero right panel */
const HeroPanel = styled.div`
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(41,171,226,.22);
  border-radius: 26px; padding: 1.5rem;
  backdrop-filter: blur(16px);
  box-shadow: 0 24px 60px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.06);
  position: relative; overflow: hidden;

  &::before {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.04), transparent);
    transform: translateX(-140%);
    animation: ${shimmerDetail} 5s ease-in-out infinite;
  }
`;
const PanelLabel = styled.div`
  display: flex; align-items: center; gap: .4rem;
  color: #29ABE2; font-size: .74rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .1em; margin-bottom: 1rem;
  &::before {
    content: ""; width: 7px; height: 7px; border-radius: 50%;
    background: #10b981; display: inline-block;
    animation: ${pulseDotDetail} 1.6s ease-in-out infinite;
  }
`;
const SignalBox = styled.div`
  display: flex; justify-content: space-between; gap: 1rem; align-items: center;
  background: rgba(255,255,255,.06); border: 1px solid rgba(41,171,226,.15);
  border-radius: 18px; padding: 1rem; margin-bottom: .9rem;
  strong { display: block; color: #fff; font-size: 1rem; }
  small { display: block; color: rgba(180,215,235,.65); margin-top: .15rem; }
  em { font-style: normal; color: #8ff0c6; font-size: .76rem; font-weight: 700; }
`;
const SignalGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.7rem;`;
const InfoMini = styled.div`
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.07);
  border-radius: 14px; padding: .85rem;
  small { display: block; color: rgba(180,215,235,.6); font-size: .72rem; margin-bottom: .2rem; }
  strong { color: #fff; font-size: .95rem; }
`;

const Grid2 = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;@media (max-width:980px){grid-template-columns:1fr;}`;
const Card = styled.div`background:var(--bg-surface);border:1px solid var(--border-color);border-radius:22px;padding:1.5rem;box-shadow:var(--shadow-sm);h3{margin:0 0 .7rem;color:var(--text-strong);}p{margin:0 0 .85rem;color:var(--text-body-soft);line-height:1.8;font-size:.95rem;}`;
const MiniIcon = styled.div`width:44px;height:44px;border-radius:14px;background:rgba(41,171,226,.12);display:flex;align-items:center;justify-content:center;color:var(--primary-color);font-size:1.15rem;margin-bottom:.85rem;`;
const Stats = styled.div`display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1rem;@media (max-width:900px){grid-template-columns:repeat(2,minmax(0,1fr));}`;
const Stat = styled.div`font-size:1.8rem;font-weight:800;color:var(--text-strong);`;
const Muted = styled.div`margin-top:.2rem;font-size:.82rem;color:var(--text-body-soft);text-transform:uppercase;letter-spacing:.06em;`;
const Bullet = styled.ul`list-style:none;padding:0;margin:0;display:grid;gap:.6rem;li{display:flex;gap:.65rem;align-items:flex-start;color:var(--text-body-soft);line-height:1.7;font-size:.94rem;}svg{color:var(--primary-color);flex-shrink:0;margin-top:.18rem;}`;
const SectionTitle = styled.h2`margin:1.2rem 0 .9rem;color:var(--text-strong);font-size:clamp(1.55rem,3vw,2.2rem);line-height:1.15;`;
const Table = styled.table`width:100%;border-collapse:collapse;th,td{text-align:left;padding:.85rem .7rem;border-bottom:1px solid var(--border-color);font-size:.92rem;}th{color:var(--text-strong);}td{color:var(--text-body-soft);}tbody tr:last-child td{border-bottom:0;}`;
const Steps = styled.ol`list-style:none;padding:0;margin:0;display:grid;gap:.75rem;li{display:flex;gap:.75rem;align-items:flex-start;color:var(--text-body-soft);line-height:1.7;font-size:.94rem;}`;
const Num = styled.span`width:30px;height:30px;border-radius:50%;background:rgba(41,171,226,.12);color:var(--primary-color);display:inline-flex;align-items:center;justify-content:center;font-weight:800;flex-shrink:0;`;
const Flow = styled.div`display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.8rem;@media (max-width:980px){grid-template-columns:1fr;}`;
const FlowItem = styled.div`background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:18px;padding:1rem;strong{display:block;color:var(--primary-color);font-size:1.1rem;margin-bottom:.3rem;}span{color:var(--text-body-soft);line-height:1.6;font-size:.92rem;}`;
const Related = styled.div`display:flex;flex-wrap:wrap;gap:.8rem;margin-top:1rem;a{display:inline-flex;align-items:center;gap:.5rem;padding:.78rem 1rem;border-radius:14px;background:var(--bg-secondary);border:1px solid var(--border-color);text-decoration:none;color:var(--primary-dark);font-weight:700;font-size:.9rem;}`;
const FaqWrap = styled.div`display:grid;gap:.9rem;`;
const FaqBtn = styled.button`width:100%;background:none;border:0;padding:0;display:flex;justify-content:space-between;gap:1rem;text-align:left;cursor:pointer;span{color:var(--text-strong);font-weight:700;line-height:1.5;font-size:.97rem;}`;
const Chevron = styled.div`color:var(--text-body-soft);transform:${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};transition:transform .2s ease;`;
const FaqBody = styled(motion.div)`overflow:hidden;div{padding-top:.8rem;color:var(--text-body-soft);line-height:1.75;font-size:.93rem;}`;
const CtaCard = styled(Card)`margin-top:1rem;background:var(--hero-gradient);h2{margin:0 0 .7rem;color:var(--hero-text) !important;-webkit-text-fill-color:var(--hero-text) !important;}p{color:var(--hero-text) !important;-webkit-text-fill-color:var(--hero-text) !important;}`;
const List = styled.ul`list-style:none;padding:0;margin:1rem 0 0;display:grid;gap:.6rem;li{display:flex;justify-content:space-between;gap:1rem;color:var(--text-body-soft);font-size:.92rem;}strong{color:var(--text-strong);}`;

/* ═══════════════════════════════════════════════════════════
   ANDROID PAGE — LIGHT THEME COMPONENTS
═══════════════════════════════════════════════════════════ */

const AndroidWrap = styled.div`
  background: var(--bg-main);
  color: var(--text-color);
  overflow-x: hidden;
  padding-bottom: 0;
`;

const PageSection = styled.section`
  padding: 4.5rem 0;
  position: relative;
  ${({ $alt }) => $alt && `
    background: var(--bg-secondary);
    &::before { content:""; position:absolute; inset:0; border-top:1px solid var(--border-color); border-bottom:1px solid var(--border-color); pointer-events:none; }
  `}
`;

const SectionHd = styled.div`
  margin-bottom: 2.4rem;
`;

const SectionTag = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--primary-color);
  margin: 0 0 0.55rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;

  &::before {
    content: "";
    display: inline-block;
    width: 18px; height: 2px;
    background: var(--primary-color); border-radius: 2px;
  }
`;

const SecTitle = styled.h2`
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  line-height: 1.13;
  letter-spacing: -0.025em;
  margin: 0;
  color: var(--text-strong);
`;

/* ── Stats ── */
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.2rem;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const AiStatCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 1.8rem 1.4rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  text-align: center;
  box-shadow: var(--shadow-sm);
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.25s;

  &::before {
    content: "";
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--primary-color), #1a7fad);
    border-radius: 22px 22px 0 0;
  }
  &::after {
    content: "";
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(41,171,226,0.06), transparent);
    transform: translateX(-140%);
    animation: ${shimmerDetail} 5s ease-in-out infinite;
  }
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 8px 32px rgba(41,171,226,0.12);
    transform: translateY(-3px);
  }
`;

const AiStatNum = styled.div`
  font-size: 2.4rem;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 0.4rem;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const AiStatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--text-body-soft);
`;

/* ── Two-column layout ── */
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.4rem;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;

/* ── Content card ── */
const AiCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 2rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 12px 40px rgba(41,171,226,0.1);
    transform: translateY(-3px);
  }
`;

const AiCardTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-strong);
  margin: 0 0 0.85rem;
  line-height: 1.4;
`;

const AiCardText = styled.p`
  font-size: 0.94rem;
  color: var(--text-body-soft);
  line-height: 1.82;
  margin: 0 0 0.85rem;
`;

const AiBullet = styled.ul`
  list-style: none;
  padding: 0; margin: 0;
  display: grid; gap: 0.65rem;

  li {
    display: flex; gap: 0.65rem;
    align-items: flex-start;
    color: var(--text-body-soft);
    line-height: 1.7; font-size: 0.93rem;
  }
  svg { color: var(--success-color); flex-shrink: 0; margin-top: 0.22rem; }
`;

/* ── Feature cards ── */
const FeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const FeatCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 1.9rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease;

  &::after {
    content: "";
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(41,171,226,0.04), transparent);
    transform: translateX(-140%);
    animation: ${shimmerDetail} 6s ease-in-out infinite var(--delay, 0s);
  }
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 12px 40px rgba(41,171,226,0.1);
    transform: translateY(-4px);
  }
`;

const FeatIconBox = styled.div`
  width: 54px; height: 54px;
  border-radius: 16px;
  background: var(--primary-light);
  border: 1px solid rgba(41,171,226,0.2);
  display: flex; align-items: center; justify-content: center;
  color: var(--primary-color); font-size: 1.35rem;
  margin-bottom: 1.1rem;
`;

const FeatCardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-strong);
  margin: 0 0 0.6rem;
  line-height: 1.4;
`;

const FeatCardText = styled.p`
  font-size: 0.9rem;
  color: var(--text-body-soft);
  line-height: 1.78;
  margin: 0;
`;

/* ── Comparison table ── */
const CmpTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.6rem;
`;

const CmpHead = styled.th`
  text-align: left;
  padding: 0.7rem 0.8rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  border-bottom: 1px solid var(--border-color);
  color: ${({ $accent }) => $accent || "var(--text-body-soft)"};
`;

const CmpCell = styled.td`
  padding: 0.75rem 0.8rem;
  font-size: 0.88rem;
  border-bottom: 1px solid var(--border-color);
  color: ${({ $good, $bad }) => $good ? "var(--success-color)" : $bad ? "#ef4444" : "var(--text-body-soft)"};
  font-weight: ${({ $good, $bad }) => ($good || $bad) ? "700" : "400"};

  tr:last-child & { border-bottom: 0; }
`;

/* ── Steps timeline ── */
const StepsTimeline = styled.div`
  display: grid;
  gap: 0;
  margin-top: 0.4rem;
`;

const StepRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.75rem 0;
  position: relative;

  ${({ $last }) => !$last && `
    &::after {
      content: "";
      position: absolute;
      left: 18px; top: 46px; bottom: -12px;
      width: 2px;
      background: linear-gradient(180deg, rgba(41,171,226,0.3), rgba(41,171,226,0.04));
    }
  `}
`;

const StepNumCircle = styled.div`
  width: 38px; height: 38px;
  border-radius: 50%;
  background: var(--primary-light);
  border: 1.5px solid rgba(41,171,226,0.3);
  color: var(--primary-color);
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 0.88rem;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  padding-top: 0.5rem;
  font-size: 0.93rem;
  color: var(--text-body-soft);
  line-height: 1.7;
`;

/* ── Ecosystem flow ── */
const FlowConnected = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { flex-direction: column; align-items: stretch; }
`;

const FlowStepCard = styled.div`
  flex: 1;
  min-width: 0;
  border-radius: 20px;
  padding: 1.6rem 1.3rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 8px 28px rgba(41,171,226,0.1);
    transform: translateY(-3px);
  }
`;

const FlowStepNum = styled.div`
  font-size: 1.6rem;
  font-weight: 900;
  background: linear-gradient(135deg, var(--primary-color), #1a7fad);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
  line-height: 1;
`;

const FlowStepText = styled.div`
  font-size: 0.88rem;
  color: var(--text-body-soft);
  line-height: 1.6;
`;

const FlowConnector = styled.div`
  color: var(--primary-color);
  opacity: 0.4;
  font-size: 1rem;
  flex-shrink: 0;
  @media (max-width: 900px) { display: none; }
`;

const EcoRelated = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.8rem;

  a {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 1.1rem; border-radius: 14px;
    background: var(--bg-secondary); border: 1px solid var(--border-color);
    text-decoration: none;
    color: var(--primary-dark) !important; -webkit-text-fill-color: var(--primary-dark) !important;
    font-weight: 600; font-size: 0.88rem;
    box-shadow: var(--shadow-sm);
    transition: background 0.22s ease, border-color 0.22s ease, transform 0.22s ease;
    &:hover { border-color: var(--primary-color); transform: translateY(-2px); box-shadow: var(--shadow-md); }
  }
`;

/* ── FAQ ── */
const FaqGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const FaqItemCard = styled.div`
  border-radius: 18px;
  overflow: hidden;
  background: var(--bg-surface);
  border: 1px solid ${({ $open }) => $open ? "var(--primary-color)" : "var(--border-color)"};
  box-shadow: var(--shadow-sm);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  &:hover { border-color: var(--primary-color); box-shadow: var(--shadow-md); }
`;

const FaqItemTrigger = styled.button`
  width: 100%;
  background: none; border: 0;
  padding: 1.1rem 1.4rem;
  display: flex; justify-content: space-between; align-items: center;
  gap: 1rem; text-align: left; cursor: pointer;
`;

const FaqItemQ = styled.span`
  color: var(--text-strong);
  font-weight: 600;
  font-size: 0.97rem;
  line-height: 1.5;
`;

const FaqChevronIcon = styled.div`
  color: var(--primary-color);
  transform: ${({ $open }) => $open ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.25s ease;
  flex-shrink: 0;
`;

const AndroidFaqBody = styled(motion.div)`
  overflow: hidden;
`;

const FaqItemA = styled.div`
  padding: 0.9rem 1.4rem 1.2rem;
  font-size: 0.93rem;
  color: var(--text-body-soft);
  line-height: 1.78;
  border-top: 1px solid var(--border-color);
`;

/* ── CTA banner — uses the site's existing hero gradient ── */
const CtaBanner = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--hero-gradient);
  padding: 6rem 0;
  text-align: center;
`;

const CtaOrbA = styled.div`
  position: absolute;
  width: 500px; height: 500px; border-radius: 50%;
  background: radial-gradient(circle, var(--hero-primary-glow) 0%, transparent 65%);
  filter: blur(60px);
  top: -150px; left: -80px; pointer-events: none;
  animation: ${floatA} 11s ease-in-out infinite;
`;

const CtaOrbB = styled.div`
  position: absolute;
  width: 380px; height: 380px; border-radius: 50%;
  background: radial-gradient(circle, var(--hero-accent-glow) 0%, transparent 65%);
  filter: blur(60px);
  bottom: -100px; right: -50px; pointer-events: none;
  animation: ${floatB} 13s ease-in-out infinite;
`;

const CtaScanLine = styled.div`
  position: absolute; left: 0; width: 100%; height: 1.5px;
  background: linear-gradient(90deg, transparent, rgba(41,171,226,0.4), rgba(247,148,29,0.25), transparent);
  animation: ${scanMoveDetail} 6s linear infinite;
  pointer-events: none;
`;

const CtaInner = styled.div`
  position: relative; z-index: 2;
  max-width: 720px; margin: 0 auto;
`;

const CtaBannerTag = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--hero-link-color);
  margin: 0 0 0.8rem;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;

  &::before, &::after {
    content: "";
    display: inline-block;
    width: 24px; height: 2px;
    background: var(--hero-link-color); border-radius: 2px;
  }
`;

const CtaBannerTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0 0 1.1rem;
  color: var(--hero-text) !important;
  -webkit-text-fill-color: var(--hero-text) !important;
`;

const CtaBannerSub = styled.p`
  font-size: 1.05rem;
  line-height: 1.82;
  color: var(--hero-text) !important;
  -webkit-text-fill-color: var(--hero-text) !important;
  opacity: 0.85;
  margin: 0 auto 2.4rem;
  max-width: 620px;
`;
